import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Languages, Rocket, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_FLAGS = { pt: '🇵🇹', en: '🇬🇧', es: '🇪🇸', fr: '🇫🇷' };
const LANGS = ['pt', 'en', 'es', 'fr'];

// Best-effort: take first non-empty line of caption as title seed
const seedFromCaption = (caption) => {
  if (!caption) return { title: '', subtitle: '' };
  const lines = caption.split('\n').map((l) => l.trim()).filter(Boolean);
  return {
    title: (lines[0] || '').slice(0, 70),
    subtitle: (lines[1] || '').slice(0, 90),
  };
};

export const PublishPromoModal = ({ open, onClose, image, caption, hashtags, platform, aspectRatio, sourceLanguage }) => {
  const seed = seedFromCaption(caption || '');
  const [title, setTitle] = useState({});
  const [subtitle, setSubtitle] = useState({});
  const [ctaLabel, setCtaLabel] = useState({
    pt: 'Reservar Agora', en: 'Book Now', es: 'Reservar Ahora', fr: 'Réserver',
  });
  const [ctaLink, setCtaLink] = useState('#booking');
  const [translating, setTranslating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [autoTranslated, setAutoTranslated] = useState(false);

  // Reset when modal opens with new image
  useEffect(() => {
    if (!open) return;
    const src = (sourceLanguage || 'en').slice(0, 2);
    setTitle({ [src]: seed.title });
    setSubtitle(seed.subtitle ? { [src]: seed.subtitle } : {});
    setAutoTranslated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, image?.data_url]);

  if (!open) return null;

  const src = (sourceLanguage || 'en').slice(0, 2);

  const handleAutoTranslate = async () => {
    const titleVal = (title[src] || '').trim();
    if (titleVal.length < 3) {
      toast.error('Escreva primeiro um título (mínimo 3 caracteres)');
      return;
    }
    setTranslating(true);
    try {
      const fields = { title: titleVal };
      const subVal = (subtitle[src] || '').trim();
      if (subVal) fields.subtitle = subVal;
      const ctaVal = (ctaLabel[src] || '').trim();
      if (ctaVal) fields.cta_label = ctaVal;

      const res = await axios.post(`${API}/marketing/translate`, {
        fields,
        source_lang: src,
        target_langs: LANGS,
      }, { timeout: 60000 });

      const t = res.data.translations || {};
      if (t.title) setTitle((prev) => ({ ...prev, ...t.title }));
      if (t.subtitle) setSubtitle((prev) => ({ ...prev, ...t.subtitle }));
      if (t.cta_label) setCtaLabel((prev) => ({ ...prev, ...t.cta_label }));
      setAutoTranslated(true);
      toast.success('Traduzido para os 4 idiomas!');
    } catch (e) {
      toast.error(`Erro na tradução: ${e.response?.data?.detail || e.message}`);
    } finally {
      setTranslating(false);
    }
  };

  const handlePublish = async () => {
    // Ensure at least one title is present
    const hasTitle = LANGS.some((l) => (title[l] || '').trim().length >= 2);
    if (!hasTitle) {
      toast.error('Pelo menos um título (numa língua) é obrigatório');
      return;
    }
    if (!image?.data_url) {
      toast.error('Imagem em falta. Gere uma imagem primeiro.');
      return;
    }
    setPublishing(true);
    try {
      const payload = {
        image_data_url: image.data_url,
        title: title,
        subtitle: subtitle,
        cta_label: ctaLabel,
        cta_link: ctaLink || '#booking',
        hashtags: hashtags || [],
        platform: platform,
        aspect_ratio: aspectRatio || '1:1',
      };
      await axios.post(`${API}/promos`, payload, { timeout: 30000 });
      toast.success('🚀 Publicado no homepage!');
      onClose(true);
    } catch (e) {
      toast.error(`Erro ao publicar: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={() => !publishing && onClose(false)}
      data-testid="publish-promo-modal"
    >
      <div
        className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-amber-50 via-white to-amber-50 px-6 py-4">
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-900">Publicar como Promo no Homepage</h2>
          </div>
          <button
            onClick={() => !publishing && onClose(false)}
            className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            data-testid="close-publish-modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="grid gap-5 p-6 md:grid-cols-[200px,1fr]">
          {/* Image preview */}
          <div className="space-y-2">
            {image?.data_url && (
              <img
                src={image.data_url}
                alt="Promo preview"
                className="aspect-square w-full rounded-lg object-cover ring-2 ring-amber-200"
              />
            )}
            <p className="text-center text-xs text-gray-500">
              {aspectRatio || '1:1'} · {platform || 'social'}
            </p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Auto-translate banner */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="mb-2 text-xs text-amber-900">
                Escreva o título em <strong>{LANG_FLAGS[src]} {src.toUpperCase()}</strong> e a IA traduz para os outros 3 idiomas automaticamente.
              </p>
              <Button
                size="sm"
                onClick={handleAutoTranslate}
                disabled={translating || !title[src]}
                className="bg-amber-600 text-white hover:bg-amber-700"
                data-testid="auto-translate-btn"
              >
                {translating ? (
                  <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> A traduzir…</>
                ) : autoTranslated ? (
                  <><Check className="mr-1.5 h-4 w-4" /> Re-traduzir</>
                ) : (
                  <><Languages className="mr-1.5 h-4 w-4" /> Traduzir para 4 idiomas</>
                )}
              </Button>
            </div>

            {/* Title per language */}
            <div>
              <Label className="mb-2 block text-sm font-semibold">Título (max 60 car.)</Label>
              <div className="space-y-2">
                {LANGS.map((l) => (
                  <div key={l} className="flex items-center gap-2">
                    <span className="w-6 text-lg">{LANG_FLAGS[l]}</span>
                    <Input
                      maxLength={70}
                      value={title[l] || ''}
                      onChange={(e) => setTitle((p) => ({ ...p, [l]: e.target.value }))}
                      placeholder={`Título em ${l.toUpperCase()}`}
                      data-testid={`promo-title-${l}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Subtitle per language */}
            <details>
              <summary className="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900">
                + Subtítulo / Kicker (opcional)
              </summary>
              <div className="mt-2 space-y-2">
                {LANGS.map((l) => (
                  <div key={l} className="flex items-center gap-2">
                    <span className="w-6 text-lg">{LANG_FLAGS[l]}</span>
                    <Input
                      maxLength={90}
                      value={subtitle[l] || ''}
                      onChange={(e) => setSubtitle((p) => ({ ...p, [l]: e.target.value }))}
                      placeholder={`Subtítulo em ${l.toUpperCase()}`}
                    />
                  </div>
                ))}
              </div>
            </details>

            {/* CTA */}
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-sm font-semibold">Botão CTA ({src.toUpperCase()})</Label>
                <Input
                  maxLength={24}
                  value={ctaLabel[src] || ''}
                  onChange={(e) => setCtaLabel((p) => ({ ...p, [src]: e.target.value }))}
                  placeholder="Reservar Agora"
                  data-testid="promo-cta-label"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Os outros 3 idiomas vêm da tradução.
                </p>
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold">Link do botão</Label>
                <Input
                  value={ctaLink}
                  onChange={(e) => setCtaLink(e.target.value)}
                  placeholder="#booking"
                  data-testid="promo-cta-link"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Use <code>#booking</code> ou um path tipo <code>/pt/blog/douro</code>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <Button variant="outline" onClick={() => onClose(false)} disabled={publishing}>
            Cancelar
          </Button>
          <Button
            onClick={handlePublish}
            disabled={publishing}
            className="bg-gradient-to-r from-amber-600 to-amber-700 text-white hover:from-amber-700 hover:to-amber-800"
            data-testid="publish-promo-confirm"
          >
            {publishing ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> A publicar…</>
            ) : (
              <><Rocket className="mr-1.5 h-4 w-4" /> Publicar no Homepage</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PublishPromoModal;
